import React, { useMemo, useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import "moment/locale/fr"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faChevronLeft,
	faChevronRight,
	faCalendarDay,
	faPlus,
} from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import { CATEGORIES, CATEGORY_ORDER, getCategory } from "./agendaCategories"
import { fetchRdvs, postRdv, deleteRdv } from "../../store/actions/agendaActions"
import { fetchAutreEntites } from "../../store/actions/autreActions"
import AgendaEvent from "./AgendaEvent"
import AgendaRdvModal from "./AgendaRdvModal"
import DepMissionV2, {
	DEPANNAGE_TYPE_APPEL_ASSISTANCE,
	DEPANNAGE_TYPE_APPEL_PRIVE,
} from "../depannage/depmission/DepMissionV2"
import { getBaseDepMission } from "../../utilities/defaultObjects"
import {
	DEP_STATE_RENDEZ_VOUS,
	DEP_TYPE_INTERVENTION,
} from "../../utilities/Addons"

// Plage horaire affichée verticalement (de 06:00 à 19:00 inclus).
const START_HOUR = 6
const END_HOUR = 19
// Hauteur d'un créneau d'une heure (px).
const SLOT_H = 56
// Hauteur totale de la zone des créneaux.
const BODY_H = (END_HOUR - START_HOUR) * SLOT_H
// Largeur de la colonne des heures.
const COL_HEURE = 64

const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi"]

// Labels d'heures : 06:00 → 19:00.
const HEURES = Array.from(
	{ length: END_HOUR - START_HOUR + 1 },
	(_, i) => START_HOUR + i
)

// Minutes depuis minuit pour un moment.
const minutes = (m) => m.hour() * 60 + m.minute()

// Répartit les événements d'un jour en colonnes (lanes) pour gérer les
// chevauchements. Mute et renvoie les events avec _lane / _lanes.
const layoutDay = (events) => {
	const sorted = [...events].sort((a, b) => a._start - b._start)
	const placed = []
	let cluster = []
	let clusterEnd = -1

	const flush = () => {
		const lanesEnd = [] // fin (min) du dernier event de chaque lane
		cluster.forEach((ev) => {
			let lane = lanesEnd.findIndex((end) => end <= ev._start)
			if (lane === -1) {
				lane = lanesEnd.length
				lanesEnd.push(ev._end)
			} else {
				lanesEnd[lane] = ev._end
			}
			ev._lane = lane
		})
		cluster.forEach((ev) => {
			ev._lanes = lanesEnd.length
			placed.push(ev)
		})
		cluster = []
		clusterEnd = -1
	}

	sorted.forEach((ev) => {
		if (cluster.length && ev._start >= clusterEnd) flush()
		cluster.push(ev)
		clusterEnd = Math.max(clusterEnd, ev._end)
	})
	flush()
	return placed
}

// Mission pré-remplie depuis un Rdv (formulaire DepMissionV2, création).
export const missionDepuisRdv = (rdv, assistancesRef, assurancesRef) => {
	const dep = rdv.depannage || {}
	// Couverture : entité type Assistance (8) → FK assistance_entite de la
	// mission ; type Assurance (1) → champ texte assurance.
	const assistance = (assistancesRef || []).find(
		(a) => a.id === dep.assurance_id
	)
	const assurance = (assurancesRef || []).find(
		(a) => a.id === dep.assurance_id
	)
	const prive = !dep.assurance_id
	return {
		...getBaseDepMission(),
		// Décision Jordan 15.10 (consigne client) : une mission née d'un Rdv
		// est une INTERVENTION par défaut, comme au dispatch (la base reste
		// Transport pour la livraison).
		type: DEP_TYPE_INTERVENTION,
		// Mission déjà planifiée : atterrit dans la grille Planning du
		// dispatch au créneau du Rdv.
		etat: DEP_STATE_RENDEZ_VOUS,
		date_rendez_vous: moment(rdv.debut).format("DD-MM-YYYY HH:mm"),
		delta_rendez_vous: rdv.duree || 60,
		appel_type: prive
			? DEPANNAGE_TYPE_APPEL_PRIVE
			: DEPANNAGE_TYPE_APPEL_ASSISTANCE,
		assistance_entite: assistance ? assistance.id : 0,
		assurance: !assistance && assurance ? assurance.label : "",
		nom: rdv.client || rdv.nom || "",
		telephone: rdv.telephone || "",
		marque: dep.marque || "",
		modele: dep.modele || "",
		plaques: dep.plaque || "",
		// Le tarif convenu vit dans total_presume ; l'info dépanneur ne
		// porte que l'encaissement (les lieux du Rdv sont en pastilles).
		// GE-66 K7 (Jordan 13.10) : pas pour un privé, tarifé au terrain
		// (un Rdv ne crée jamais de livraison).
		total_presume: prive ? 0 : dep.tarif || 0,
		info_depanneur: dep.encaisser_sur_place
			? "Encaisser sur place"
			: "",
	}
}

const Agenda = () => {
	const history = useHistory()
	const dispatch = useDispatch()

	// Lundi de la semaine affichée.
	const [lundi, setLundi] = useState(() => moment().startOf("isoWeek"))

	// Catégories visibles (togglers). Toutes activées par défaut.
	const [visibles, setVisibles] = useState(() => new Set(CATEGORY_ORDER))

	// Modal de création/édition : { open, prefill, editing(rdv|null) }.
	const [modal, setModal] = useState({ open: false, prefill: {}, editing: null })

	// Heure courante (pour la ligne « maintenant »), rafraîchie chaque minute.
	const [now, setNow] = useState(() => moment())
	useEffect(() => {
		const t = setInterval(() => setNow(moment()), 60000)
		return () => clearInterval(t)
	}, [])

	// Les jours de la semaine (lundi → vendredi).
	const semaine = useMemo(
		() => JOURS.map((_, i) => lundi.clone().add(i, "days")),
		[lundi]
	)

	// Rdv de la semaine, chargés depuis l'API (slice agendaInfo).
	const rdvs = useSelector((state) => state.agendaInfo.rdvs)

	// (Re)charge les Rdv de la semaine affichée. Plage [lundi, lundi+7[ en ISO.
	const rechargerSemaine = () =>
		dispatch(
			fetchRdvs({
				start: lundi.clone().startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
				end: lundi
					.clone()
					.add(7, "days")
					.startOf("day")
					.format("YYYY-MM-DDTHH:mm:ss"),
			})
		)

	useEffect(() => {
		rechargerSemaine()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lundi])

	const changerSemaine = (delta) =>
		setLundi((prev) => prev.clone().add(delta, "weeks"))
	const allerAujourdhui = () => setLundi(moment().startOf("isoWeek"))

	const toggleCategorie = (key) =>
		setVisibles((prev) => {
			const next = new Set(prev)
			next.has(key) ? next.delete(key) : next.add(key)
			return next
		})

	// Événements visibles, regroupés par index de jour, avec géométrie + lanes.
	const eventsParJour = useMemo(() => {
		const parJour = JOURS.map(() => [])
		rdvs
			.filter((r) => visibles.has(r.type))
			.forEach((r) => {
				const debut = moment(r.debut)
				const idx = semaine.findIndex((d) => d.isSame(debut, "day"))
				if (idx === -1) return
				const start = minutes(debut)
				parJour[idx].push({
					...r,
					_start: start,
					_end: start + r.duree,
					_top: ((start - START_HOUR * 60) / 60) * SLOT_H,
					_height: (r.duree / 60) * SLOT_H,
				})
			})
		return parJour.map((evs) => layoutDay(evs))
	}, [rdvs, visibles, semaine])

	const ouvrirRdv = (rdv) => {
		// Rdv concrétisé (lien vers une fiche métier) → on y navigue ; sinon, édition.
		const route = getCategory(rdv.type).route(rdv)
		if (route) history.push(route)
		else setModal({ open: true, prefill: {}, editing: rdv })
	}

	const ouvrirCreation = (prefill = {}) =>
		setModal({ open: true, prefill, editing: null })
	const fermerModal = () =>
		setModal({ open: false, prefill: {}, editing: null })

	// Création/édition : POST puis rechargement de la semaine.
	const enregistrerRdv = (payload) =>
		dispatch(postRdv(payload)).then((res) => {
			if (res) {
				rechargerSemaine()
				fermerModal()
			}
		})

	// --- Concrétisation Rdv dépannage → mission de dispatch ----------------
	// Le formulaire mission s'ouvre pré-rempli depuis le Rdv ; les lieux
	// (texte libre côté Rdv) sont à re-saisir en PointInteret, le texte du Rdv
	// est reporté dans « Info pour dépanneur ».
	const [rdvAConcretiser, setRdvAConcretiser] = useState(null)
	const assurancesRef = useSelector((state) => state.autreInfo.assurances)
	const assistancesRef = useSelector((state) => state.autreInfo.assistances)
	useEffect(() => {
		if (!assistancesRef || assistancesRef.length === 0)
			dispatch(fetchAutreEntites())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const rdvToMission = (rdv) =>
		missionDepuisRdv(rdv, assistancesRef, assurancesRef)

	// Mission créée → le Rdv passe en statut « concrétisé » + lien de trace.
	const apresConcretisation = (mission) => {
		const rdv = rdvAConcretiser
		setRdvAConcretiser(null)
		if (!rdv || !mission || !mission.id) return
		dispatch(
			postRdv({
				id: rdv.id,
				type: rdv.type,
				statut: "concretise",
				debut: rdv.debut,
				duree: rdv.duree,
				nom: rdv.nom,
				entite_id: rdv.entite_id,
				telephone: rdv.telephone,
				titre: rdv.titre,
				commentaire: rdv.commentaire,
				depannage: rdv.depannage,
				dep_mission_id: mission.id,
			})
		).then(() => rechargerSemaine())
	}

	const ouvrirConcretisation = (rdv) => {
		fermerModal()
		setRdvAConcretiser(rdv)
	}

	const supprimerRdv = (id) =>
		dispatch(deleteRdv({ id })).then((res) => {
			if (res) {
				rechargerSemaine()
				fermerModal()
			}
		})

	// Clic sur un créneau vide → pré-remplit jour + heure (arrondie à 30 min).
	const clicCreneau = (date) => (e) => {
		const rect = e.currentTarget.getBoundingClientRect()
		const y = e.clientY - rect.top
		let total = START_HOUR * 60 + (y / SLOT_H) * 60
		total = Math.round(total / 30) * 30
		total = Math.max(START_HOUR * 60, Math.min(END_HOUR * 60 - 30, total))
		const h = Math.floor(total / 60)
		const m = total % 60
		ouvrirCreation({
			date: date.format("YYYY-MM-DD"),
			heure: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
		})
	}

	const aujourdhui = moment()
	// Position verticale de la ligne « maintenant » (px), visible seulement si
	// l'heure courante est dans la plage affichée [START_HOUR, END_HOUR].
	const nowMinutes = minutes(now)
	const nowTop = ((nowMinutes - START_HOUR * 60) / 60) * SLOT_H
	const nowVisible =
		nowMinutes >= START_HOUR * 60 && nowMinutes <= END_HOUR * 60
	const dernierJour = semaine[semaine.length - 1].clone().locale("fr")
	const lundiFr = lundi.clone().locale("fr")
	const memeMois = lundi.month() === dernierJour.month()
	const libelleSemaine = memeMois
		? `${lundiFr.format("D")} – ${dernierJour.format("D MMMM YYYY")}`
		: `${lundiFr.format("D MMM")} – ${dernierJour.format("D MMM YYYY")}`

	return (
		<div style={styles.page}>
			{/* Barre de navigation de la semaine */}
			<div style={styles.header}>
				<div>
					<div style={styles.surtitre}>Planning</div>
					<h2 style={styles.titre}>{libelleSemaine}</h2>
				</div>
				<div style={styles.nav}>
					<button
						type="button"
						style={styles.newBtn}
						onClick={() => ouvrirCreation()}
					>
						<FontAwesomeIcon icon={faPlus} />
						<span>Nouveau</span>
					</button>
					<button
						type="button"
						style={styles.navBtn}
						onClick={() => changerSemaine(-1)}
						title="Semaine précédente"
					>
						<FontAwesomeIcon icon={faChevronLeft} />
					</button>
					<button
						type="button"
						style={styles.todayBtn}
						onClick={allerAujourdhui}
					>
						<FontAwesomeIcon icon={faCalendarDay} />
						<span>Aujourd'hui</span>
					</button>
					<button
						type="button"
						style={styles.navBtn}
						onClick={() => changerSemaine(1)}
						title="Semaine suivante"
					>
						<FontAwesomeIcon icon={faChevronRight} />
					</button>
				</div>
			</div>

			{/* Togglers de catégories */}
			<div style={styles.togglers}>
				{CATEGORY_ORDER.map((key) => {
					const cat = CATEGORIES[key]
					const actif = visibles.has(key)
					return (
						<button
							key={key}
							type="button"
							onClick={() => toggleCategorie(key)}
							style={{
								...styles.toggler,
								borderColor: actif ? cat.color : COLORS.border,
								backgroundColor: actif ? cat.bg : COLORS.surface,
								color: actif ? cat.color : COLORS.textLight,
								opacity: actif ? 1 : 0.6,
							}}
						>
							<span
								style={{
									...styles.pastille,
									backgroundColor: actif
										? cat.color
										: COLORS.borderStrong,
								}}
							/>
							{cat.label}
						</button>
					)
				})}
			</div>

			{/* En-tête des jours */}
			<div style={styles.entetes}>
				<div style={{ width: COL_HEURE, flexShrink: 0 }} />
				{semaine.map((date, i) => {
					const estAujourdhui = date.isSame(aujourdhui, "day")
					return (
						<div
							key={`jour-${i}`}
							style={{
								...styles.enteteJour,
								...(estAujourdhui ? styles.enteteJourActif : {}),
							}}
						>
							<span style={styles.jourNom}>{JOURS[i]}</span>
							<span
								style={{
									...styles.jourDate,
									...(estAujourdhui ? styles.jourDateActif : {}),
								}}
							>
								{date.format("D")}
							</span>
						</div>
					)
				})}
			</div>

			{/* Corps de la grille */}
			<div style={styles.corps}>
				{/* Colonne des heures */}
				<div style={{ width: COL_HEURE, flexShrink: 0, position: "relative", height: BODY_H }}>
					{HEURES.map((heure) => (
						<div
							key={`h-${heure}`}
							style={{
								...styles.labelHeure,
								top: (heure - START_HOUR) * SLOT_H,
							}}
						>
							{String(heure).padStart(2, "0")}:00
						</div>
					))}
				</div>

				{/* Colonnes des jours */}
				{semaine.map((date, i) => {
					const estAujourdhui = date.isSame(aujourdhui, "day")
					return (
						<div
							key={`col-${i}`}
							onClick={clicCreneau(date)}
							style={{
								...styles.colJour,
								height: BODY_H,
								cursor: "pointer",
								backgroundColor: estAujourdhui
									? COLORS.accentLight
									: COLORS.surface,
							}}
						>
							{/* Lignes horaires */}
							{HEURES.map((heure) => (
								<div
									key={`l-${i}-${heure}`}
									style={{
										...styles.ligneHeure,
										top: (heure - START_HOUR) * SLOT_H,
									}}
								/>
							))}

							{/* Ligne « maintenant » (colonne du jour, semaine courante) */}
							{estAujourdhui && nowVisible && (
								<div style={{ ...styles.ligneNow, top: nowTop }} />
							)}

							{/* Événements */}
							{eventsParJour[i].map((ev) => (
								<AgendaEvent
									key={ev.id}
									rdv={ev}
									top={ev._top}
									height={ev._height}
									lane={ev._lane}
									lanes={ev._lanes}
									onClick={() => ouvrirRdv(ev)}
								/>
							))}
						</div>
					)
				})}
			</div>

			{modal.open && (
				<AgendaRdvModal
					prefill={modal.prefill}
					editing={modal.editing}
					onClose={fermerModal}
					onSave={enregistrerRdv}
					onDelete={supprimerRdv}
					onConcretiser={ouvrirConcretisation}
				/>
			)}

			{/* Concrétisation : formulaire mission pré-rempli depuis le Rdv. */}
			{rdvAConcretiser && (
				<DepMissionV2
					visible
					closeModal={() => setRdvAConcretiser(null)}
					refreshDeps={() => {}}
					editDepMission={rdvToMission(rdvAConcretiser)}
					onSaved={apresConcretisation}
					rdvHints={{
						lieu: (rdvAConcretiser.depannage || {}).lieu || "",
						destination:
							(rdvAConcretiser.depannage || {}).destination ||
							"",
					}}
				/>
			)}
		</div>
	)
}

const styles = {
	page: {
		...gs.page,
		padding: "24px 32px",
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 16,
	},
	surtitre: {
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.1em",
		textTransform: "uppercase",
		marginBottom: 4,
	},
	titre: {
		fontSize: 24,
		fontWeight: 600,
		color: COLORS.text,
		letterSpacing: "-0.02em",
		textTransform: "capitalize",
		margin: 0,
	},
	nav: { display: "flex", alignItems: "center", gap: 8 },
	newBtn: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		height: 38,
		padding: "0 18px",
		borderRadius: 10,
		border: "none",
		backgroundColor: COLORS.text, // noir #1C1917 (primaire projet)
		color: "#FFFFFF",
		cursor: "pointer",
		fontSize: 14,
		fontWeight: 600,
		fontFamily: "inherit",
		marginRight: 4,
	},
	navBtn: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: 38,
		height: 38,
		borderRadius: 10,
		border: `0.5px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		cursor: "pointer",
		fontSize: 14,
	},
	todayBtn: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		height: 38,
		padding: "0 16px",
		borderRadius: 10,
		border: `0.5px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		cursor: "pointer",
		fontSize: 14,
		fontWeight: 500,
	},
	togglers: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 },
	toggler: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		height: 32,
		padding: "0 14px",
		borderRadius: 99,
		border: "0.5px solid",
		cursor: "pointer",
		fontSize: 13,
		fontWeight: 600,
		fontFamily: "inherit",
		transition: "opacity .15s",
	},
	pastille: { width: 8, height: 8, borderRadius: 99, flexShrink: 0 },
	entetes: {
		display: "flex",
		borderTop: `0.5px solid ${COLORS.border}`,
		borderLeft: `1px solid ${COLORS.border}`,
		borderRight: `0.5px solid ${COLORS.border}`,
		borderTopLeftRadius: 16,
		borderTopRightRadius: 16,
		overflow: "hidden",
		backgroundColor: COLORS.surface,
	},
	enteteJour: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: 4,
		padding: "12px 8px",
		borderLeft: `1px solid ${COLORS.border}`,
	},
	enteteJourActif: { backgroundColor: COLORS.accentLight },
	jourNom: {
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
		letterSpacing: "0.04em",
		textTransform: "uppercase",
	},
	jourDate: {
		fontSize: 18,
		fontWeight: 600,
		color: COLORS.text,
		lineHeight: 1,
	},
	jourDateActif: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		minWidth: 28,
		height: 28,
		borderRadius: 99,
		backgroundColor: COLORS.accent,
		color: "#FFFFFF",
	},
	corps: {
		display: "flex",
		border: `0.5px solid ${COLORS.border}`,
		borderBottomLeftRadius: 16,
		borderBottomRightRadius: 16,
		overflow: "hidden",
		backgroundColor: COLORS.surface,
	},
	labelHeure: {
		position: "absolute",
		right: 10,
		transform: "translateY(-50%)",
		fontSize: 11,
		fontWeight: 500,
		color: COLORS.textLight,
		whiteSpace: "nowrap",
	},
	colJour: {
		flex: 1,
		position: "relative",
		borderLeft: `1px solid ${COLORS.border}`,
	},
	ligneHeure: {
		position: "absolute",
		left: 0,
		right: 0,
		borderTop: `0.5px solid ${COLORS.border}`,
	},
	ligneNow: {
		position: "absolute",
		left: 0,
		right: 0,
		height: 0,
		borderTop: `2px solid ${COLORS.danger}`,
		zIndex: 5,
		pointerEvents: "none",
	},
}

export default Agenda
